import Export16 from '@carbon/icons-react/es/export/16';
import ListChecked20 from '@carbon/icons-react/es/list--checked/20';
import { useBoolean } from 'ahooks';
import { Tooltip } from 'antd';
import { debounce, keyBy } from 'lodash';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { ExportQueueStatus } from 'api/async-export';
import { OrderType } from 'api/models';
import { Button } from 'components/atoms/button';
import { Column, Selectable } from 'components/atoms/grid';
import { IconWrapper } from 'components/atoms/icon-shell/icon-shell';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { SidebarCollapseButton } from 'components/molecules/sidebar-collapse-button/SidebarCollapseButton';
import { toastService } from 'components/molecules/toast';
import { useCurrentView } from 'components/molecules/views/current-view-state';
import {
  GroupFilterMenu,
  useAllTasksKanbanGroupBy,
} from 'components/organisms/filter-menu-sidebar';
import {
  asColumnOrder,
  cleanUpColumnOrder,
  ColumnListPopover,
  ColumnOrder,
  GridChangeEvent,
} from 'components/organisms/grid-custom-column';
import { AllTasks } from 'components/organisms/tasks';
import { COLUMN_ORDER_SERIALIZER } from 'components/organisms/tasks/column-order-serializer';
import { FilterContainer } from 'components/organisms/tasks/components';
import {
  allTasksFiltersLocalStorageKey,
  excludedAllTasksFields,
  TASK_LIST_COLUMNS_STATE_LOCALSTORAGE_KEY,
} from 'components/organisms/tasks/constants';
import { exportAsCSV } from 'components/organisms/tasks/export';
import { FilterableField } from 'components/organisms/tasks/fields';
import { GroupedFilterView } from 'components/organisms/tasks/grouped-filter-view';
import {
  getDefaultColumnOrder,
  useTaskAndProjectColumns,
} from 'components/organisms/tasks/use-columns';
import { useFiltersCaching } from 'components/organisms/tasks/use-filters-caching';
import { useGroupFilters } from 'components/organisms/tasks/use-group-filters';
import {
  default_column_order,
  DEFAULT_COLUMN_ORDER,
  stringToColumnOrder,
  useServerSideDataSource,
} from 'components/organisms/tasks/utils';
import { AllTasksViewSwitcher } from 'components/pages/all-tasks/all-tasks-view-switcher';
import { GroupFieldsMeta } from 'components/pages/filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { Argon } from 'components/templates';
import { TOGGLE_ALLTASKS_SIDEBAR } from 'constant/constant';
import { RootState } from 'data';
import { useAccount } from 'hooks/use-account';
import { useDistractionFreeMode } from 'hooks/use-distraction-free-mode';
import { PrereleaseFeature, useFeature } from 'hooks/use-feature';
import {
  addUpdateColumnOrderIndexedDb,
  getColumnOrderFromIndexedDb,
} from 'hooks/use-idb';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useQueryJiraConfig } from 'queries/jira-integration';
import { FieldsStore, ProjectField, TaskField } from 'stores/fields';
import { withValidCustomTaskFields } from 'utils/duplicate-fields-transformer';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import { SelectionControls } from '../selection-controls';
import { useSelections } from '../use-selections';
import { useAllTasksRefresh } from './use-all-tasks-refresh';
import { useAllTasksSocketUpdates } from './use-all-tasks-socket-updates';
import { useFieldsAsGroupedFields } from './use-fields-as-grouped-fields';

const EMPTY_RECORD = {};

const excludedColumnsForReordering = ['checkbox', 'status'];

export const AllTasksPage: FC = () => {
  const title = useTranslation('all_tasks.title');
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const { data: jiraIntegrationConfig } = useQueryJiraConfig();
  const { accountId } = useAccount();

  const excludedFields = useMemo(() => {
    // remove jiraIntegration from excluded fields ONLY when the integration is active
    if (jiraIntegrationConfig?.length === 0) {
      return [...excludedAllTasksFields, 'jiraIntegration'];
    }

    return excludedAllTasksFields;
  }, [jiraIntegrationConfig]);

  const excludedFieldsForColumns = useMemo(
    () => [
      ...excludedFields,
      'private',
      'completedLateBy',
      'phaseName',
      'taskDependencyStatus',
    ],
    [excludedFields],
  );

  const excludedFieldsForFilters = useMemo(
    () => [...excludedFields, 'projectPhaseId'],
    [excludedFields],
  );

  const projectFieldsExcludedForFilters = useMemo(
    () => ['customerMembersInvited', 'customerMembersAccepted'],
    [],
  );

  const [fields, fieldsForFilter] = useMemo(() => {
    const taskFields = FieldsStore.enabledTaskFields(fieldsStore).map((field) =>
      withValidCustomTaskFields(accountId, field),
    );

    return [
      taskFields.filter(
        ({ fieldName }) => !excludedFieldsForColumns.includes(fieldName),
      ),
      taskFields.filter(
        ({ fieldName }) => !excludedFieldsForFilters.includes(fieldName),
      ),
    ];
  }, [
    fieldsStore,
    accountId,
    excludedFieldsForColumns,
    excludedFieldsForFilters,
  ]);
  const { fields: projectFields, loading: isProjectFieldLoading } =
    useProjectFieldsAsFilterableFields();

  const projectFieldForFilter = useMemo(() => {
    return projectFields.filter(
      ({ fieldName }) => !projectFieldsExcludedForFilters.includes(fieldName),
    );
  }, [projectFields, projectFieldsExcludedForFilters]);

  const { groupedFields } = useFieldsAsGroupedFields({
    taskFields: fieldsForFilter,
    projectFields: projectFieldForFilter,
  });
  const taskFieldsLookupById = useMemo(
    () => keyBy(fields, 'fieldId') as Record<number, TaskField>,
    [fields],
  );
  const projectFieldsLookupById = useMemo(
    () =>
      keyBy(isProjectFieldLoading ? [] : projectFields, 'fieldId') as Record<
        number,
        ProjectField
      >,
    [isProjectFieldLoading, projectFields],
  );

  const taskFilterFieldsLookupByName = useMemo(
    () =>
      fieldsForFilter.length > 0
        ? (keyBy(fieldsForFilter, 'fieldName') as Record<string, TaskField>)
        : EMPTY_RECORD,
    [fieldsForFilter],
  );
  const taskColumnFieldsLookupByName = useMemo(
    () =>
      fields.length > 0
        ? (keyBy(fields, 'fieldName') as Record<string, TaskField>)
        : EMPTY_RECORD,
    [fields],
  );
  const fieldsLookupForSort = useMemo(
    () =>
      fields.length > 0
        ? (keyBy(fields, 'fieldName') as Record<number, TaskField>)
        : EMPTY_RECORD,
    [fields],
  );
  const fieldsLoading =
    !FieldsStore.loaded(fieldsStore) || isProjectFieldLoading;

  /**
   * Commenting the progress bar since it blocks main thread and gives app frozen experience.
   * Adding a temporary local state to pass progress prop.
   * TODO: to analyse the progress bar.
   */
  // const { progress, start, complete } = useProgress(100);
  const [progress, setProgress] = useState(0);
  const start = useCallback(() => setProgress(0), [setProgress]);
  const complete = useCallback(() => setProgress(100), [setProgress]);
  const disableSocketUpdate = useFeature(PrereleaseFeature.DisableSocketUpdate);
  const projectsFieldsLookupByName = useMemo(
    (): Record<string, FilterableField> =>
      keyBy(isProjectFieldLoading ? [] : projectFields, 'fieldName'),
    [projectFields, isProjectFieldLoading],
  );
  const groupLookupForFilter: GroupFieldsMeta = useMemo(
    () => [
      {
        id: FilterGroupType.Task,
        fields: taskFilterFieldsLookupByName,
      },
      {
        id: FilterGroupType.Project,
        fields: projectsFieldsLookupByName,
      },
    ],
    [taskFilterFieldsLookupByName, projectsFieldsLookupByName],
  );
  const groupLookupForColumns: GroupFieldsMeta = useMemo(
    () => [
      {
        id: FilterGroupType.Task,
        fields: taskColumnFieldsLookupByName,
      },
      {
        id: FilterGroupType.Project,
        fields: projectsFieldsLookupByName,
      },
    ],
    [taskColumnFieldsLookupByName, projectsFieldsLookupByName],
  );

  const { filter, filterFunction, clearFilters, selectFilter } =
    useGroupFilters(groupLookupForFilter, FilterGroupType.Task, !fieldsLoading);
  const includedProjectColumns = useMemo(
    () =>
      filter.groupCriteria
        .filter((x) => x.id === FilterGroupType.Project)
        .map((x) => x.criteria.field.fieldName),
    [filter.groupCriteria],
  );

  const selectableRef = useRef<Selectable>(null);
  const { selected, setSelected, count, reset, selectAll, isSelectedAll } =
    useSelections(selectableRef);
  const isPaginated = !window.Cypress;

  const [
    isExportInProgress,
    { setTrue: setExportStarted, setFalse: setExportEnded },
  ] = useBoolean(false);

  const translate = useTranslate();

  useAllTasksRefresh(start, complete, !isPaginated);
  useAllTasksSocketUpdates({
    isEnable: !disableSocketUpdate && !isPaginated, // enable socket update only both are unchecked
    onExportCompleted: setExportEnded,
    isExportInProgress,
  });
  useFiltersCaching({
    localStorageKey: allTasksFiltersLocalStorageKey,
  });

  const [groupBy, setGroupBy] = useAllTasksKanbanGroupBy(
    taskFilterFieldsLookupByName,
    fieldsLoading,
  );
  const [columnOrderV2] = useLocalStorage<string[]>(
    TASK_LIST_COLUMNS_STATE_LOCALSTORAGE_KEY,
    default_column_order,
    COLUMN_ORDER_SERIALIZER,
  );
  const { viewId: currentViewId } = useCurrentView({
    defaultViewId: -1,
  });
  const masterColumnOrder = useMemo(
    () => columnOrderV2.map(stringToColumnOrder) || DEFAULT_COLUMN_ORDER,
    [columnOrderV2],
  );

  const columns: Column[] = useTaskAndProjectColumns(
    taskFieldsLookupById,
    projectFieldsLookupById,
    translate,
    includedProjectColumns,
  );

  const { onGridReady, gridApi, isInitialLoading, gridColumnApi } =
    useServerSideDataSource(
      taskFieldsLookupById,
      projectFieldsLookupById,
      filter,
      fieldsLookupForSort,
    );

  const defaultColumnState: ColumnOrder[] = useMemo(
    () => getDefaultColumnOrder(columns),
    [columns],
  );
  const [order, setOrder] = useState(
    cleanUpColumnOrder(defaultColumnState)(masterColumnOrder),
  );

  useEffect(() => {
    async function getColOrder() {
      const lsOrder = JSON.parse(
        localStorage.getItem(
          `all_tasks.v3.viewId=${currentViewId}.column_order`,
        ) || '[]',
      );
      const idbData = await getColumnOrderFromIndexedDb(
        `all_tasks.v1.view=${currentViewId}`,
      );
      let data =
        idbData && idbData.columnOrder.length > 0 ? idbData.columnOrder : [];

      if (lsOrder && lsOrder.length > 0) {
        data = lsOrder;
        localStorage.removeItem(
          `all_tasks.v3.viewId=${currentViewId}.column_order`,
        );
      }

      if (data && data?.length > 0) {
        if (defaultColumnState && defaultColumnState.length > 0) {
          setOrder(cleanUpColumnOrder(defaultColumnState)(data));
        } else {
          setOrder(data);
        }
      }
    }
    getColOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewId]);

  const [columnOrder, setColumnOrder] = useState<ColumnOrder[]>(order);

  const exportAsCsvWithToast = () => {
    setExportStarted();
    exportAsCSV({
      filter,
      columnOrder,
      lookup: groupLookupForColumns,
      failExport: setExportEnded,
    })
      .then((status) => {
        if (status === ExportQueueStatus.Submitted) {
          toastService({
            title: translate('async.exports.in_progress.title'),
            message: translate('async.exports.in_progress.message'),
          }).success();
        }
      })
      .catch(() => {
        toastService({
          title: translate('async.exports.error.title'),
          message: translate('async.exports.error.message'),
        }).error();
      });
  };

  const blockEffect = useRef(false);
  // Called when user changes view - changedColumnOrder is sent from backend
  const handleColumnOrderChangedByView = useCallback(
    (changedColumnOrder: ColumnOrder[]) => {
      blockEffect.current = true;
      setColumnOrder(
        cleanUpColumnOrder(defaultColumnState)(changedColumnOrder),
      );
    },
    [defaultColumnState],
  );

  useEffect(() => {
    if (blockEffect.current) {
      return;
    }
    setColumnOrder(order);
  }, [currentViewId, order]);

  useEffect(() => {
    // Used to update the grid with latest columnOrder
    if (!gridColumnApi) {
      return;
    }
    gridColumnApi?.setColumnState(columnOrder);
  }, [columnOrder, gridColumnApi]);

  useEffect(() => {
    async function addUpdateColOrder() {
      await addUpdateColumnOrderIndexedDb(
        `all_tasks.v1.view=${currentViewId}`,
        currentViewId,
        order,
        OrderType.AllTasks,
      );
    }
    addUpdateColOrder();
  }, [currentViewId, order]);

  // Called whenever grid is altered to update columnOrder state and LocalStorage
  const handleGridChange = useCallback(
    (event: GridChangeEvent) => {
      if (event) {
        const newColumnOrder = asColumnOrder(event.columnApi.getColumnState());

        setColumnOrder(newColumnOrder);
        setOrder(newColumnOrder);
      }
    },
    [setOrder],
  );

  const throttledGridChange = useMemo(
    () => debounce(handleGridChange, 100),
    [handleGridChange],
  );

  const { active, exit, enter } = useDistractionFreeMode(
    TOGGLE_ALLTASKS_SIDEBAR,
  );

  function sidebarToggle() {
    if (active) {
      exit();
    } else {
      enter();
    }
  }

  return (
    <Argon
      isDistractionFree={active}
      title={<HeaderWithIconTitle icon={<ListChecked20 />} title={title} />}
      sider={
        <GroupFilterMenu
          fields={fields}
          lookup={groupLookupForFilter}
          taskLookup={taskFilterFieldsLookupByName}
          filter={filter}
          selectFilter={selectFilter}
          clearFilters={clearFilters}
          isFieldsLoading={fieldsLoading}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          columnOrder={columnOrder}
          handleColumnOrderChange={handleColumnOrderChangedByView}
          defaultColumnOrder={defaultColumnState}
          masterColumnOrder={masterColumnOrder}
        />
      }
      progress={progress}
      actions={
        count > 0
          ? {
              primary: (
                <SelectionControls
                  count={count}
                  reset={reset}
                  selectAll={isPaginated ? undefined : selectAll}
                />
              ),
            }
          : {
              primary: (
                <FilterContainer>
                  <SidebarCollapseButton sidebarToggle={sidebarToggle} />

                  <GroupedFilterView
                    isFieldsLoading={fieldsLoading}
                    filter={filter}
                    fields={groupedFields}
                    selectFilter={selectFilter}
                    clearFilters={clearFilters}
                    lookup={groupLookupForFilter}
                    taskLookup={taskFilterFieldsLookupByName}
                    columnOrder={columnOrder}
                    defaultColumnOrder={defaultColumnState}
                    handleColumnOrderChange={handleColumnOrderChangedByView}
                  />
                </FilterContainer>
              ),
              secondary: (
                <>
                  <ColumnListPopover
                    columnOrder={columnOrder}
                    gridColumnApi={gridColumnApi}
                    excludedColumns={excludedColumnsForReordering}
                    defaultColumnOrder={default_column_order}
                  />
                  <Tooltip
                    title={<FormattedMessage id="export.tooltip" />}
                    placement="topLeft"
                  >
                    <Button
                      icon={
                        <IconWrapper>
                          <Export16 />
                        </IconWrapper>
                      }
                      type="text"
                      size="small"
                      loading={isExportInProgress}
                      onClick={exportAsCsvWithToast}
                    />
                  </Tooltip>
                </>
              ),
              central: <AllTasksViewSwitcher />,
            }
      }
      content={
        <AllTasks
          taskFields={taskFieldsLookupById}
          fields_loading={fieldsLoading}
          progress={!isPaginated ? progress : fieldsLoading ? 0 : 100}
          filterFunction={isPaginated ? null : filterFunction}
          selected={selected}
          onSelectionChange={setSelected}
          selectableRef={selectableRef}
          isSelectedAll={isSelectedAll}
          isPaginationEnabled={isPaginated}
          columns={columns}
          gridApi={gridApi}
          onGridReady={onGridReady}
          isInitialLoading={isInitialLoading}
          handleGridChange={throttledGridChange}
        />
      }
    />
  );
};
